// Hardcoded config for the one-off "Vidamor Chapter 7.5" experience (/vidamor).
// Deliberately not generalized — a later slice can lift this into event data.
// Every unknown value is a clearly-marked placeholder ('' or '#'); VidamorPage
// degrades gracefully when a value is empty (hides the button / shows a
// coming-soon line in the page's voice). See PLACEHOLDERS below.

export interface VidamorConfig {
  /** Seeded on dev by the orchestrator; the money path + standing store key off it. */
  eventId: string;
  /** Community slug for the stranger apply-to-join flow. */
  communitySlug: string;
  /** get-shwifty reveal video (iframe embed URL, 16:9). */
  videoUrl: string;
  /** "what?" GIF/image URL. */
  gifUrl: string;
  /** you're-in WhatsApp group invite (new tab). */
  whatsappUrl: string;
  /** "Helper bird" apply link (new tab); the last, non-tier ladder row. */
  helperApplyUrl: string;
  /** Price label shown on the Helper bird row (a label, not a real tier price). */
  helperPriceLabel: string;
  /** you're-in idea box — a Google Form, embedded in a popover. */
  ideaBoxUrl: string;
  /** you're-in complaints — the coping-with-life video, played in cinema mode (iframe embed URL). */
  complaintsVideoUrl: string;
  /** Dress code copy, shown in the dress-code popover. */
  dressCode: string;
  /** Dress-code Pinterest board, embedded in the same popover once set. */
  dressCodePinterestUrl: string;
  /** "when?" display string (the date — also used in the you're-in sub-line). */
  whenDisplay: string;
  /** "when?" second line (doors-to-close times); shown in the facts row only. */
  whenTimeDisplay: string;
  /** IANA timezone the naive schedule times belong to (calendar links). */
  timezone: string;
  /** "where?" display string. */
  whereDisplay: string;
  /** Dodges before the get-shwifty button is caught and plays. */
  dodgeLimit: number;
}

// All placeholders resolved 2026-07-13 (specs/vidamor-release). whereDisplay
// stays an intentional teaser — the real venue lives on the event in Convex,
// members-only via server-side redaction. The Pinterest board is currently
// secret: the link-out works (invite URL), the inline embed lights up only if
// the team flips the board public.
export const VIDAMOR: VidamorConfig = {
  eventId: 'vidamor-chapter-7-5',
  communitySlug: 'vidamor',
  // Rick and Morty — "Get Schwifty" (starts at 0:03, plays in the cinema modal).
  videoUrl: 'https://www.youtube-nocookie.com/embed/I1188GO4p1E?start=3&autoplay=1&rel=0',
  // Self-hosted (public/), downloaded from Tenor ("kido") — no hotlink risk.
  gifUrl: '/vidamor-what.gif',
  whatsappUrl: 'https://chat.whatsapp.com/LRTP4Z9ji8DHSUFajbsubf?s=cl&p=i&ilr=0',
  // "Helper bird" application Google Form (verified publicly accessible).
  helperApplyUrl:
    'https://docs.google.com/forms/d/e/1FAIpQLSdSN9_VP4YgYEy5M-1tlpv9-Xn0AWJfP11A0kbtU5IBu3Uhyg/viewform',
  helperPriceLabel: '€22',
  // "Vidamor Idea Box" Google Form (lives in the opentelos account).
  ideaBoxUrl:
    'https://docs.google.com/forms/d/e/1FAIpQLSf-G6eZrCujbdjTjtQDy_mX0GdK7q-sGzWwk2rw0L1Z2oPLew/viewform',
  // The coping-with-life video (starts at 0:11, plays in cinema mode).
  complaintsVideoUrl: 'https://www.youtube-nocookie.com/embed/5K0Q2v9Q6Ss?start=11&autoplay=1&rel=0',
  dressCode: "Come as your most shwifty self. If in doubt: colour, comfort, and something you can dance in.",
  // "chapter-75" board (resolved from pin.it/7l9dde0kB). Secret board: this
  // invite URL opens for humans; the inline embed needs the board public.
  dressCodePinterestUrl:
    'https://de.pinterest.com/thzse5t213123125345/chapter-75/?invite_code=44bb39a3578e4af390942c9e1871e97e&sender=368732425649436058',
  whenDisplay: 'August 22, 2026',
  whenTimeDisplay: '14:00 till 07:00',
  timezone: 'Europe/Lisbon',
  whereDisplay: "Lisbon City",
  dodgeLimit: 3
};

/** A config URL is "real" (usable as a link) when it isn't blank or the '#' placeholder. */
export function isRealUrl(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.trim().length > 0 && url.trim() !== '#';
}
